/**
 * School/category slug for AJAX: meta tag (always on category shop HTML) then ThemeJS.
 * admin-ajax.php requests have no URL context, so POST must carry this value.
 */
function commencementModalCategoryContext() {
  const meta = document.querySelector('meta[name="commencement-category-context"]');
  const fromMeta =
    meta && meta.getAttribute("content") ? meta.getAttribute("content").trim() : "";
  if (fromMeta) {
    return fromMeta;
  }
  if (
    typeof ThemeJS !== "undefined" &&
    ThemeJS.Variables &&
    ThemeJS.Variables.CategoryContext
  ) {
    const s = String(ThemeJS.Variables.CategoryContext).trim();
    if (s) {
      return s;
    }
  }
  return "";
}

/**
 * API-driven selects: enable add-to-cart when the combo matches a catalog row with is_enabled.
 * If the WC product is variable, also require a resolved WC variation_id + attribute map.
 * If the WC product is simple, catalog options only — cart uses clinel + commencement_modal_attrs.
 */
function initCommencementModalVariations(root) {
  const form = root.querySelector("form.commencement-variations-form");
  if (!form) {
    return;
  }
  const requiresWcVariation =
    form.getAttribute("data-commencement-requires-wc-variation") === "1";
  const lookupEl = form.querySelector(".commencement-modal-lookup-json");
  const btn = form.querySelector(".single_add_to_cart_button");
  const vidInput = form.querySelector("#commencement_modal_variation_id");
  const attrsJson = form.querySelector("#commencement_modal_attrs_json");
  const wcWrap = form.querySelector("#commencement-wc-attr-fields");
  const selects = [...form.querySelectorAll("select.commencement-attr-select")];
  const addToWrap = form.querySelector(".commencement-modal-add-to-row");
  const unavailableMsg = form.querySelector(".commencement-modal-unavailable-msg");

  const liveEl = document.getElementById(
    "commencement-modal-variation-debug-live",
  );

  let lookup = [];
  try {
    lookup = lookupEl ? JSON.parse(lookupEl.textContent || "[]") : [];
  } catch (err) {
    console.warn("[commencement modal] lookup JSON", err);
    if (liveEl) {
      liveEl.textContent = `[commencement API form] lookup JSON parse error: ${err && err.message ? err.message : err}`;
    }
    return;
  }

  function comboFromSelects() {
    const o = {};
    selects.forEach((sel) => {
      const name = sel.getAttribute("data-commencement-attr-name");
      if (name && sel.value) {
        o[name] = sel.value;
      }
    });
    return o;
  }

  /** Mirrors PHP commencement_modal_normalize_attr_key_for_match: trim, strip tags, collapse space, lower case. */
  function normalizeAttrKeyForMatch(s) {
    const t = String(s || "")
      .replace(/<[^>]*>/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
    return t;
  }

  /** Same idea as simplistics_normalize_attr_assoc (trim keys/values, stable key order). */
  function normalizeAttrAssocForMatch(obj) {
    const out = {};
    Object.keys(obj || {}).forEach((k) => {
      const v = obj[k];
      if (v === "" || v == null) {
        return;
      }
      out[normalizeAttrKeyForMatch(k)] = normalizeAttrKeyForMatch(v);
    });
    const sorted = {};
    Object.keys(out)
      .sort()
      .forEach((k) => {
        sorted[k] = out[k];
      });
    return sorted;
  }

  function attrAssocMapsEqual(a, b) {
    return (
      JSON.stringify(normalizeAttrAssocForMatch(a)) ===
      JSON.stringify(normalizeAttrAssocForMatch(b))
    );
  }

  function findRow(combo) {
    return lookup.find((row) => attrAssocMapsEqual(row.api || {}, combo));
  }

  /** Match PHP commencement_modal_parse_variation_row_is_enabled + JSON quirks. */
  function rowIsEnabled(row) {
    if (!row || typeof row !== "object") {
      return false;
    }
    let v;
    if (Object.prototype.hasOwnProperty.call(row, "is_enabled")) {
      v = row.is_enabled;
    } else if (Object.prototype.hasOwnProperty.call(row, "enabled")) {
      v = row.enabled;
    } else if (Object.prototype.hasOwnProperty.call(row, "isEnabled")) {
      v = row.isEnabled;
    } else {
      return false;
    }
    if (typeof v === "boolean") {
      return v;
    }
    if (v === 0 || v === "0") {
      return false;
    }
    if (v === 1 || v === "1") {
      return true;
    }
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      if (s === "false" || s === "no" || s === "off" || s === "") {
        return false;
      }
      if (s === "true" || s === "yes" || s === "on") {
        return true;
      }
    }
    return Boolean(v);
  }

  function clearUnavailableMsg() {
    if (!unavailableMsg) {
      return;
    }
    unavailableMsg.textContent = "";
    unavailableMsg.setAttribute("hidden", "");
  }

  /** @param {'msgNotEnabled' | 'msgNoMapping' | 'msgNoMatch'} dataKey */
  function setUnavailableMsg(dataKey) {
    if (!unavailableMsg || !addToWrap) {
      return;
    }
    const text = addToWrap.dataset[dataKey];
    if (!text) {
      clearUnavailableMsg();
      return;
    }
    unavailableMsg.textContent = text;
    unavailableMsg.removeAttribute("hidden");
  }

  function setButtonEnabled(on) {
    if (!btn) {
      return;
    }
    btn.disabled = !on;
    if (on) {
      btn.classList.remove("disabled");
      btn.removeAttribute("aria-disabled");
    } else {
      btn.classList.add("disabled");
      btn.setAttribute("aria-disabled", "true");
    }
  }

  function refresh() {
    if (!vidInput) {
      return;
    }
    const combo = comboFromSelects();
    const complete =
      selects.length > 0 && selects.every((s) => s.value && s.value !== "");

    if (attrsJson) {
      attrsJson.value = complete ? JSON.stringify(combo) : "";
    }
    if (wcWrap) {
      wcWrap.innerHTML = "";
    }
    clearUnavailableMsg();

    if (!complete) {
      vidInput.value = "";
      setButtonEnabled(false);
      if (liveEl) {
        liveEl.textContent = [
          `[commencement API form] incomplete selection`,
          `selects=${selects.length} combo=${JSON.stringify(combo)}`,
        ].join("\n");
      }
      return;
    }

    const row = findRow(combo);
    const enabled = rowIsEnabled(row);
    const wcMappedOk =
      !requiresWcVariation ||
      (Boolean(row) &&
        parseInt(String(row.variation_id), 10) > 0 &&
        row.wc_attributes &&
        Object.keys(row.wc_attributes).length > 0);
    const ok = Boolean(row && enabled && wcMappedOk);

    if (row && !enabled) {
      setUnavailableMsg("msgNotEnabled");
    } else if (row && enabled && !wcMappedOk) {
      setUnavailableMsg("msgNoMapping");
    } else if (!row) {
      setUnavailableMsg("msgNoMatch");
    } else {
      clearUnavailableMsg();
    }

    if (!ok) {
      vidInput.value = "";
      setButtonEnabled(false);
    }

    if (liveEl) {
      const parts = [
        `[commencement API form] selects=${selects.length} complete=${complete}`,
        `combo=${JSON.stringify(combo)}`,
        row
          ? `row: requires_wc_variation=${requiresWcVariation} is_enabled(raw)=${JSON.stringify(row.is_enabled)} coerced=${enabled} variation_id=${row.variation_id} wc_attr_count=${row.wc_attributes ? Object.keys(row.wc_attributes).length : 0}`
          : "row: (no lookup match — combo vs api keys/values differ? check trim/casing)",
        `add_to_cart_enabled=${!!(row && ok)}`,
      ];
      if (row && row.resolution_debug) {
        parts.push(
          `resolution_debug (server):\n${JSON.stringify(row.resolution_debug, null, 2)}`,
        );
      }
      liveEl.textContent = parts.join("\n");
    }

    if (!ok) {
      return;
    }

    vidInput.value =
      requiresWcVariation && parseInt(String(row.variation_id), 10) > 0
        ? String(row.variation_id)
        : "";
    if (wcWrap && requiresWcVariation && row.wc_attributes) {
      Object.keys(row.wc_attributes).forEach((tax) => {
        const inp = document.createElement("input");
        inp.type = "hidden";
        inp.name = tax.startsWith("attribute_") ? tax : `attribute_${tax}`;
        inp.value = row.wc_attributes[tax];
        wcWrap.appendChild(inp);
      });
    }
    setButtonEnabled(true);
  }

  selects.forEach((sel) => sel.addEventListener("change", refresh));
  refresh();
}

document.addEventListener("DOMContentLoaded", () => {
  const modal = document.getElementById("product-modal");
  const contentArea = document.getElementById("modal-content");

  if (!modal || !contentArea) {
    return;
  }

  const skeletonTemplate = document.getElementById("product-modal-skeleton-template");

  // The dialog markup ships with aria-hidden="true"; without toggling it the open
  // modal is invisible to assistive tech while its controls stay focusable.
  const modalFocusTrap =
    typeof focusTrap !== "undefined"
      ? focusTrap.createFocusTrap(modal, {
          escapeDeactivates: true,
          clickOutsideDeactivates: true,
          returnFocusOnDeactivate: true,
          // Skeleton content has nothing tabbable on the first frame.
          fallbackFocus: modal.querySelector(".modal-container"),
          onDeactivate() {
            closeProductModal(false);
          },
        })
      : null;

  function openProductModal() {
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    modal.removeAttribute("inert");
    if (modalFocusTrap) {
      modalFocusTrap.activate();
    } else {
      modal.querySelector(".modal-close")?.focus();
    }
  }

  function closeProductModal(deactivateTrap = true) {
    if (deactivateTrap && modalFocusTrap && modal.classList.contains("is-open")) {
      // onDeactivate() re-enters here to finish the teardown.
      modalFocusTrap.deactivate();
      return;
    }
    modal.classList.remove("is-open", "is-bundle-modal");
    modal.setAttribute("aria-hidden", "true");
    modal.setAttribute("inert", "");
    modal.querySelector(".modal-container")?.style.removeProperty("--bundle-modal-width");
    contentArea.innerHTML = "";
  }

  // Closed on load: keep the a11y tree and tab order in sync before any interaction.
  modal.setAttribute("inert", "");

  // Bundle_Modal reuses this dialog element, so it needs the same open/close path.
  window.ThemeJS = window.ThemeJS || {};
  window.ThemeJS.Product_Modal = {
    open: openProductModal,
    close: closeProductModal,
  };

  document.body.addEventListener("click", async (e) => {
    const trigger = e.target.closest(".modal-trigger");
    if (!trigger) {
      return;
    }
    e.preventDefault();
    const productId = trigger.dataset.id;
    const lineTitle = trigger.dataset.lineTitle || "";

    // Show skeleton immediately, then modal
    if (skeletonTemplate) {
      contentArea.innerHTML = skeletonTemplate.innerHTML;
    }
    openProductModal();

    // Fetch data from WordPress
    const formData = new FormData();
    formData.append("action", "get_product_modal_content");
    formData.append("product_id", productId);
    if (lineTitle) {
      formData.append("line_title", lineTitle);
    }
    const commencementLoop = trigger.getAttribute("data-commencement-loop");
    if (commencementLoop) {
      formData.append("commencement_loop", commencementLoop);
    }
    if (typeof console !== "undefined" && console.debug) {
      let parsed = null;
      try {
        parsed = commencementLoop ? JSON.parse(commencementLoop) : null;
      } catch (e) {
        parsed = { parseError: String(e && e.message ? e.message : e) };
      }
      console.debug("[commencement modal] trigger payload", {
        productId,
        lineTitle,
        categoryContext: commencementModalCategoryContext(),
        dataCommencementLoopAttrLength: commencementLoop ? commencementLoop.length : 0,
        dataCommencementLoopAttrHead: commencementLoop
          ? commencementLoop.slice(0, 200)
          : "",
        parsedPreview: parsed,
      });
    }
    const categoryCtx = commencementModalCategoryContext();
    if (categoryCtx) {
      formData.append("category_context", categoryCtx);
    }

    const ajaxUrl =
      (typeof ThemeJS !== "undefined" && ThemeJS?.Variables?.Ajax?.url) ||
      (typeof wc_add_to_cart_params !== "undefined" &&
        wc_add_to_cart_params?.ajax_url) ||
      "/wp-admin/admin-ajax.php";

    try {
      const response = await fetch(ajaxUrl, {
        method: "POST",
        body: formData,
      });
      const html = await response.text();

      if (!response.ok) {
        contentArea.innerHTML = "Failed to load product.";
        return;
      }

      // Inject HTML and re-trigger WooCommerce Variation scripts
      contentArea.innerHTML = html;

      initCommencementModalVariations(contentArea);

      if (!contentArea.querySelector("form.commencement-variations-form")) {
        const variationsForm = contentArea.querySelector("form.variations_form");
        if (variationsForm) {
          const addToCartBtn = variationsForm.querySelector(
            ".single_add_to_cart_button",
          );
          if (addToCartBtn) {
            addToCartBtn.classList.add("disabled");
            addToCartBtn.setAttribute("aria-disabled", "true");
          }
        }
      }

      const $form =
        typeof jQuery !== "undefined" &&
        jQuery(contentArea)
          .find(".variations_form")
          .not(".commencement-variations-form");
      if (
        $form &&
        $form.length &&
        typeof $form.wc_variation_form === "function"
      ) {
        $form.wc_variation_form();
      }
    } catch (error) {
      contentArea.innerHTML = "Failed to load product.";
      console.error("Product modal error:", error);
    }
  });

  modal.addEventListener("click", (e) => {
    if (modal.classList.contains("is-bundle-modal")) {
      if (e.target.closest(".commencement-bundle-modal, .modal-close")) {
        if (e.target.closest(".modal-close")) {
          closeProductModal();
        }
        return;
      }
    }

    const modalContainer = modal.querySelector(".modal-container");
    const isCloseBtn = e.target.closest(".modal-close");
    const clickedOutside = modalContainer && !modalContainer.contains(e.target);

    if (clickedOutside || isCloseBtn) {
      closeProductModal();
    }
  });

  // Escape closes the dialog even when focus-trap is unavailable.
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && modal.classList.contains("is-open")) {
      closeProductModal();
    }
  });

  // Swallow clicks on disabled Add to Cart so WooCommerce’s alert never runs
  contentArea.addEventListener(
    "click",
    (e) => {
      const btn = e.target.closest(".single_add_to_cart_button");
      if (btn && (btn.disabled || btn.classList.contains("disabled"))) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true,
  );

  // Listen for the Add to Cart submission inside the modal
  let isSubmitting = false;
  document.body.addEventListener("submit", async (e) => {
    if (
      !e.target.classList.contains("variations_form") &&
      !e.target.classList.contains("cart")
    ) {
      return;
    }
    // Only handle forms inside our modal
    if (!modal.contains(e.target)) {
      return;
    }

    e.preventDefault();
    if (isSubmitting) return;
    isSubmitting = true;

    const form = e.target;
    const btn = form.querySelector(".single_add_to_cart_button");

    const requiresWcVariation =
      form.getAttribute("data-commencement-requires-wc-variation") === "1";
    const variationIdInput = form.querySelector(
      'input[name="variation_id"], input.variation_id',
    );
    const variationId =
      variationIdInput && parseInt(variationIdInput.value, 10) > 0
        ? variationIdInput.value
        : null;
    if (
      form.classList.contains("variations_form") &&
      requiresWcVariation &&
      !variationId
    ) {
      isSubmitting = false;
      return;
    }

    const isCommencementApi = form.classList.contains(
      "commencement-variations-form",
    );

    // Use WooCommerce's built-in add_to_cart endpoint (handles both simple & variable)
    const addToCartUrl =
      typeof wc_add_to_cart_params !== "undefined" &&
      wc_add_to_cart_params.wc_ajax_url
        ? wc_add_to_cart_params.wc_ajax_url.replace(
            "%%endpoint%%",
            "add_to_cart",
          )
        : window.location.origin + "/?wc-ajax=add_to_cart";

    // Build minimal POST data – quantity always 1
    const formData = new FormData();
    const productIdInput = form.querySelector('input[name="product_id"]');
    const parentProductId = productIdInput ? productIdInput.value : "";
    // Variable modal historically posts variation id as product_id when set; simple uses parent id only.
    formData.set("product_id", variationId ? variationId : parentProductId);
    formData.set("quantity", "1");
    if (variationId) {
      formData.set("variation_id", variationId);
    }

    const categoryCtx = commencementModalCategoryContext();
    if (categoryCtx) {
      formData.set("category_context", categoryCtx);
    }

    if (isCommencementApi) {
      const clinelInp = form.querySelector('input[name="clinel"]');
      if (clinelInp && clinelInp.value) {
        formData.set("clinel", clinelInp.value);
      }
      const attrsInp = form.querySelector("#commencement_modal_attrs_json");
      if (attrsInp && attrsInp.value) {
        formData.set("commencement_modal_attrs", attrsInp.value);
      }
    }

    if (variationId) {
      form.querySelectorAll('select[name^="attribute_"]').forEach((sel) => {
        if (sel.name && sel.value) {
          formData.set(sel.name, sel.value);
        }
      });
      form
        .querySelectorAll('#commencement-wc-attr-fields input[name^="attribute_"]')
        .forEach((inp) => {
          if (inp.name && inp.value) {
            formData.set(inp.name, inp.value);
          }
        });
    }

    console.log("formData", formData);

    if (btn) {
      console.log("btn", btn);
      btn.classList.remove("added");
      btn.classList.add("loading");
      btn.disabled = true;
    }

    try {
      const response = await fetch(addToCartUrl, {
        method: "POST",
        body: formData,
      });
      const rawText = await response.text();
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch (parseErr) {
        console.error(
          "[commencement modal] add_to_cart non-JSON response",
          response.status,
          rawText.slice(0, 500),
        );
      }

      if (data.error) {
        console.error("[commencement modal] add_to_cart error payload", data);
        alert(
          data.product_url
            ? "Could not add to cart. Please check your options."
            : "Could not add to cart. Please try again."
        );
        return;
      }

      // Success: refresh cart fragments and close modal
      if (data.fragments && typeof jQuery !== "undefined") {
        jQuery.each(data.fragments, (key, html) => {
          jQuery(key).replaceWith(html);
        });
        // Always notify so Cart.js can sync the bag badge (do not require cart_hash).
        // Pass null for $button so WC does not inject a "View cart" link into the modal.
        // The fourth argument names the item for Cart.js's toast.
        const titleEl = contentArea.querySelector(".product-modal-info h2");
        jQuery(document.body).trigger("added_to_cart", [
          data.fragments,
          data.cart_hash || "",
          null,
          { name: titleEl ? titleEl.textContent.trim() : "" },
        ]);
      }

      if (btn) {
        btn.classList.remove("loading");
        btn.classList.add("added");
      }

      // Brief pause so the checkmark is visible before the modal closes
      await new Promise((resolve) => setTimeout(resolve, 450));

      closeProductModal();
    } catch (err) {
      console.error("Add to cart error:", err);
      //alert("Could not add to cart. Please try again.");
    } finally {
      isSubmitting = false;
      if (btn && modal.classList.contains("is-open")) {
        btn.classList.remove("loading");
        btn.disabled = false;
      }
    }
  });
});
